import React, { useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Users, Plus, X, Edit3, Trash2, Check, Shield, Camera, Image as ImageIcon } from "lucide-react";
import { useLanguage } from "../LanguageProvider";
import { useAuth } from "../AuthProvider";
import { useAdmin } from "../AdminProvider";
import { supabase } from "../services/supabase";
import toast from "react-hot-toast";
import { Header } from "../components/layout/Header";
import { PageLoader } from "../components/common/LoadingStates";
import { ConfirmModal } from "../components/common/ConfirmModal";
import { MorphingPageDots } from "../components/common/MorphingPageDots";

export const UserManagement = () => {
  const { t } = useLanguage();
  const { isAuthenticated, linkedParticipantId } = useAuth();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();
  const { allParticipants, refreshGlobalData, loading } = useOutletContext();

  const [newParticipant, setNewParticipant] = useState("");
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [editName, setEditName] = useState("");
  const [editProfileImage, setEditProfileImage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState(null);
  const [editPreviewUrl, setEditPreviewUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const addParticipant = async (e) => {
    e.preventDefault();
    if (!newParticipant.trim()) return;

    const { error } = await supabase
      .from("participants")
      .insert([{ name: newParticipant.trim() }]);

    if (!error) {
      setNewParticipant("");
      refreshGlobalData();
      toast.success(t("add_success"));
    } else {
      toast.error(t("add_failed"));
    }
  };

  const confirmDeleteParticipant = async () => {
    if (!participantToDelete) return;
    const { error } = await supabase.from("participants").delete().eq("id", participantToDelete);
    if (!error) {
      refreshGlobalData();
      toast.success(t("player_deleted"));
    } else {
      toast.error(t("error_occurred"));
    }
  };

  const deleteParticipant = (id) => {
    setParticipantToDelete(id);
    setShowConfirm(true);
  };

  const startEditParticipant = (participant) => {
    setEditingParticipant(participant.id);
    setEditName(participant.name);
    setEditProfileImage(null);
    setEditPreviewUrl(null);
  };

  // Generate preview URL for selected image file
  React.useEffect(() => {
    if (editProfileImage) {
      const url = URL.createObjectURL(editProfileImage);
      setEditPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setEditPreviewUrl(null);
    }
  }, [editProfileImage]);

  const handleRemoveImage = () => {
    setEditProfileImage(null);
    setEditPreviewUrl(null);
  };

  const uploadProfileImage = async (file, participantId) => {
    try {
      const fileName = `${participantId}-${Date.now()}.${file.name.split(".").pop()}`;
      const { error: uploadError } = await supabase.storage
        .from("profile-images")
        .upload(fileName, file, { cacheControl: "3600", upsert: false });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from("profile-images").getPublicUrl(fileName);
      return data.publicUrl;
    } catch (error) {
      toast.error(`${t("image_upload_error")}: ${error.message}`);
      return null;
    }
  };

  const saveParticipantChanges = async (participantId) => {
    if (!editName.trim() || isSaving) {
      toast.error(t("empty_name_error"));
      return;
    }

    setIsSaving(true);
    try {
      let imageUrl = null;
      if (editProfileImage) {
        toast.loading(t("uploading"), { id: "upload" });
        imageUrl = await uploadProfileImage(editProfileImage, participantId);
        if (!imageUrl) toast.dismiss("upload");
      }

      const updateData = { name: editName.trim() };
      if (imageUrl) updateData.profile_image_url = imageUrl;

      const { error } = await supabase
        .from("participants")
        .update(updateData)
        .eq("id", participantId);

      if (error) throw error;

      toast.success(t("save_success"), { id: "upload" });
      setEditingParticipant(null);
    } catch (error) {
      toast.error(`${t("save_changes_failed")}: ${error.message}`);
    } finally {
      setIsSaving(false);
      setEditName("");
      setEditProfileImage(null);
      refreshGlobalData();
    }
  };

  const paginatedParticipants = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return allParticipants.slice(startIndex, startIndex + itemsPerPage);
  }, [allParticipants, currentPage]);

  const totalPages = Math.ceil(allParticipants.length / itemsPerPage);

  // Reset to first page if total pages change and current page is out of bounds
  React.useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [allParticipants.length, totalPages, currentPage]);

  return (
    <PageLoader loading={loading}>
      <div className="users-page">
        <Header title={t("player_list")} />
        <div className="container">
          <div className="card">
            <div className="card-header">
              <h2>{isAuthenticated ? t("add_player") : t("player_list")}</h2>
              <button className="btn-icon" onClick={() => navigate(-1)}><X size={24} /></button>
            </div>

            <div className="card-body">
              {isAuthenticated && (
                <div className="management-toolbar">
                  <form onSubmit={addParticipant} className="add-player-toolbar">
                    <div className="toolbar-input-wrapper">
                      <Users size={18} className="toolbar-icon" />
                      <input
                        type="text"
                        placeholder={t("add_new_player_placeholder")}
                        value={newParticipant}
                        onChange={(e) => setNewParticipant(e.target.value)}
                        className="toolbar-input"
                      />
                    </div>
                    <button type="submit" className="btn-toolbar-add">
                      <Plus size={18} />
                      <span>{t("add_player")}</span>
                    </button>
                  </form>
                </div>
              )}

              <div className="users-list-wrapper">
                <div className="users-grid-utility">
                  {paginatedParticipants.map((participant) => (
                    <div key={participant.id} className={`user-row-utility ${editingParticipant === participant.id ? 'is-editing' : ''}`}>
                      {editingParticipant === participant.id ? (
                        <div className="user-edit-premium">
                          {/* Avatar Preview Section */}
                          <div className="edit-avatar-zone">
                            <label className="edit-avatar-preview" title={t("change_photo")}>
                              {editPreviewUrl ? (
                                <img src={editPreviewUrl} alt="Preview" className="edit-avatar-img" />
                              ) : participant.profile_image_url ? (
                                <img src={participant.profile_image_url} alt={participant.name} className="edit-avatar-img" />
                              ) : (
                                <div className="edit-avatar-initials">
                                  {(editName || participant.name).charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div className="edit-avatar-overlay">
                                <Camera size={16} />
                              </div>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  if (e.target.files[0]) setEditProfileImage(e.target.files[0]);
                                }}
                                style={{ display: "none" }}
                              />
                            </label>
                            {(editPreviewUrl || participant.profile_image_url) && editPreviewUrl !== null && (
                              <button
                                type="button"
                                className="edit-avatar-remove"
                                onClick={handleRemoveImage}
                                title={t("remove_photo")}
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>

                          {/* Name Input + Actions */}
                          <div className="edit-fields-column">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="input input-edit-name"
                              placeholder={t("name_placeholder")}
                              autoFocus
                            />
                            {editPreviewUrl && (
                              <div className="edit-image-badge">
                                <ImageIcon size={12} />
                                <span>{t("image_selected")}</span>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="edit-actions-group">
                            <button
                              onClick={() => saveParticipantChanges(participant.id)}
                              className="btn-save-check"
                              title={t("save")}
                              disabled={isSaving}
                            >
                              <Check size={18} />
                            </button>
                            <button
                              onClick={() => { setEditingParticipant(null); setEditPreviewUrl(null); setEditProfileImage(null); }}
                              className="btn-cancel-x"
                              title={t("cancel")}
                            >
                              <X size={18} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="user-main-info">
                            <div className={`user-avatar-compact ${participant.linked_user_id ? 'avatar-ring-verified' : ''}`}>
                              {participant.profile_image_url ? (
                                <img src={participant.profile_image_url} alt={participant.name} />
                              ) : (
                                <div className="avatar-initials-utility">{participant.name.charAt(0).toUpperCase()}</div>
                              )}
                              {participant.linked_user_id && (
                                <span className="verified-dot"><Check size={7} /></span>
                              )}
                            </div>
                            <span className="user-name-utility">{participant.name}</span>
                            {participant.linked_user_id && (
                              <span className="verified-badge">
                                <Shield size={10} />
                                <span className="verified-badge-text">{t("verified_player")}</span>
                              </span>
                            )}
                          </div>

                          {(isAdmin || isAuthenticated) && (
                            <div className="user-actions-utility">
                              {(isAdmin || participant.id === linkedParticipantId) && (
                                <button
                                  onClick={() => startEditParticipant(participant)}
                                  className="btn-action-ghost"
                                  title={t("edit")}
                                >
                                  <Edit3 size={16} />
                                </button>
                              )}
                              {(isAdmin || !participant.linked_user_id || participant.id === linkedParticipantId) && (
                                <button
                                  onClick={() => deleteParticipant(participant.id)}
                                  className="btn-action-ghost danger"
                                  title={t("delete")}
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="pagination-wrapper-utility">
                    <div className="pagination-info-utility">
                      {t("showing")} {((currentPage - 1) * itemsPerPage) + 1}–{Math.min(currentPage * itemsPerPage, allParticipants.length)} {t("of")} {allParticipants.length}
                    </div>
                    <MorphingPageDots
                      total={totalPages}
                      activeIndex={currentPage}
                      onChange={setCurrentPage}
                    />
                  </div>
                )}
              </div>
              {allParticipants.length === 0 && (
                <div className="empty-state">
                  <Users size={48} />
                  <p>{t("no_players_yet")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={confirmDeleteParticipant}
        title={t("delete_player_title")}
        message={t("confirm_delete_player")}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        type="danger"
      />
    </PageLoader>
  );
};
